import React from "react";
//#region types
import { CustomerFormTypes } from "models/formTypes";
import { CreateOnChangeHandlersProps } from "models/propTypes/customerPropTypes";
//#endregion
//#region components
import { useCustomerService } from "serviceHooks";
import { useAPIRequestKeyRef } from "components/common";
import { useDebouncedCallback } from "use-debounce";
//#endregion
import { ServiceHelper, UtilsHelper } from "utils/helpers";
import { CommonConstants } from "utils/constants";

const CreateOnChangeHandlers: React.FC<CreateOnChangeHandlersProps> = ({
  values,
  connect,
  selectedCustomerChangeHandler,
  organizationNumberChangeHandler,
  isFormDirty,
  onFormValidationChange,
}) => {
  const customerService = useCustomerService();

  const onCustomerNameChange = useDebouncedCallback((name: string) => {
    selectedCustomerChangeHandler(name);
  }, 1000);

  React.useEffect(() => {
    onCustomerNameChange(values.generalInfo?.name ?? "");
  }, [values.generalInfo?.name]);

  const onOrganizationNumberChange = useDebouncedCallback((orgNumber?: string) => {
    const orgNumberFormatted =
      orgNumber && !orgNumber.includes("-")
        ? UtilsHelper.applyMask(orgNumber, CommonConstants.OrganizationNumberMask)
        : orgNumber;

    organizationNumberChangeHandler(orgNumberFormatted);
  }, 1000);

  React.useEffect(() => {
    if (!isFormDirty) return;

    onOrganizationNumberChange(values.generalInfo?.orgNumber);
  }, [values.generalInfo?.orgNumber]);

  const { apiRequestKeyRef: customerApiKeyRef, updateKey } = useAPIRequestKeyRef();

  const checkCustomerDuplicate = useDebouncedCallback(async (customerDetails?: CustomerFormTypes.GeneralInfo) => {
    const currentKey = updateKey();
    onFormValidationChange?.(false); // disable footer until the duplicate check is done

    const alreadyExistsFieldProps = connect("alreadyExists");
    if (customerDetails?.name && customerDetails?.billingAddress?.city) {
      alreadyExistsFieldProps.setValue(undefined);
      const { data: existingCustomer, key } = await ServiceHelper.withAPIRequestKey(
        customerService.checkCustomerExists({
          id: values.id,
          name: customerDetails.name,
          billingAddress: customerDetails.billingAddress,
        }),
        currentKey
      );

      // Check if the API request key matches the current key
      if (customerApiKeyRef.current === key) {
        const isDuplicateCustomer = existingCustomer.id !== undefined;
        alreadyExistsFieldProps.setValue(isDuplicateCustomer);
        onFormValidationChange?.(true); // enable footer after the duplicate check is done
      }
    } else {
      alreadyExistsFieldProps.setValue(false);
    }
  }, 1000);

  React.useEffect(() => {
    if (!isFormDirty) {
      return;
    }
    checkCustomerDuplicate(values.generalInfo);
  }, [
    values.generalInfo?.name,
    values.generalInfo?.billingAddress?.street1,
    values.generalInfo?.billingAddress?.street2,
    values.generalInfo?.billingAddress?.zipCode,
    values.generalInfo?.billingAddress?.city,
    values.generalInfo?.billingAddress?.countryCode,
  ]);

  return <></>;
};

export default CreateOnChangeHandlers;
